import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LinkIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon,
  ArrowPathIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
  UsersIcon,
  BuildingLibraryIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { integrationsAPI, paymentGatewayAPI } from '@/services/api'
import AsaasConfigModal from '@/components/integrations/AsaasConfigModal'
import PagarmeConfigModal from '@/components/integrations/PagarmeConfigModal'

interface Integration {
  id: number
  name: string
  type: 'bank' | 'payment_gateway' | 'accounting' | 'crm' | 'other'
  provider: string
  status: 'active' | 'inactive' | 'error' | 'pending'
  last_sync: string
  auto_sync: boolean
  sync_frequency: number
  config: {
    api_key?: string
    api_secret?: string
    environment?: 'sandbox' | 'production'
    webhook_url?: string
    [key: string]: any
  }
  stats: {
    total_syncs: number
    last_sync_records: number
    total_records: number
    error_count: number
  }
  created_at: string
}

interface IntegrationStats {
  total_integrations: number
  active_integrations: number
  total_synced_records: number
  sync_errors: number
  most_active_integration: string
  last_global_sync: string
}

interface BankConnection {
  id: number
  bank_name: string
  connection_status: 'connected' | 'disconnected' | 'error'
  account_count: number
  last_transaction_sync: string
  connector_id: number
}

const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate()
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [paymentGateways, setPaymentGateways] = useState<any[]>([])
  const [stats, setStats] = useState<IntegrationStats | null>(null)
  const [bankConnections, setBankConnections] = useState<BankConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showGatewayModal, setShowGatewayModal] = useState(false)
  const [selectedGateway, setSelectedGateway] = useState<string | null>(null)
  const [editingGateway, setEditingGateway] = useState<any | null>(null)

  // Novos states para dados reais do gateway
  const [expandedGateway, setExpandedGateway] = useState<number | null>(null)
  const [gatewayData, setGatewayData] = useState<{ [key: number]: { balance: any; transactions: any[] } }>({})
  const [loadingData, setLoadingData] = useState<{ [key: number]: boolean }>({})
  const [syncingToBank, setSyncingToBank] = useState<{ [key: number]: boolean }>({})

  const fetchIntegrationsData = async () => {
    try {
      setLoading(true)
      // Connect to REAL API endpoints
      const [integrationsData, statsData, bankData, gatewaysData] = await Promise.all([
        integrationsAPI.getIntegrations().catch(() => []),
        integrationsAPI.getStats().catch(() => null),
        integrationsAPI.getBankConnections().catch(() => []),
        paymentGatewayAPI.getGateways().catch(() => [])
      ])
      setIntegrations(integrationsData)
      setStats(statsData)
      setBankConnections(bankData)
      setPaymentGateways(gatewaysData)
      setError(null)
    } catch (err: any) {
      console.error('Error fetching integrations:', err)
      setError(`Erro ao carregar integrações: ${err.message}`)
      // Fallback to mock data
      setIntegrations(mockIntegrations)
      setStats(mockStats)
      setBankConnections(mockBankConnections)
      setPaymentGateways([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchIntegrationsData()
  }, [])

  const handleGatewaySuccess = () => {
    setShowGatewayModal(false)
    setSelectedGateway(null)
    setEditingGateway(null)
    fetchIntegrationsData()
  }

  // Buscar dados reais do gateway (saldo e transações)
  const fetchGatewayData = async (gatewayId: number) => {
    try {
      setLoadingData(prev => ({ ...prev, [gatewayId]: true }))

      // Buscar saldo e transações em paralelo
      const [balanceData, transactionsData] = await Promise.all([
        paymentGatewayAPI.getGatewayBalance(gatewayId.toString()).catch(() => null),
        paymentGatewayAPI.getGatewayTransactions(gatewayId.toString(), 30, 10).catch(() => ({ transactions: [] }))
      ])

      setGatewayData(prev => ({
        ...prev,
        [gatewayId]: {
          balance: balanceData,
          transactions: transactionsData.transactions || []
        }
      }))
    } catch (err) {
      console.error('Erro ao buscar dados do gateway:', err)
    } finally {
      setLoadingData(prev => ({ ...prev, [gatewayId]: false }))
    }
  }

  // Sincronizar gateway com sistema bancário
  const syncGatewayToBank = async (gatewayId: number) => {
    try {
      setSyncingToBank(prev => ({ ...prev, [gatewayId]: true }))

      const result = await paymentGatewayAPI.syncGatewayToBank(gatewayId.toString())

      if (result.success) {
        alert(`✅ Sincronização concluída!\n\n` +
              `💰 Conta bancária criada/atualizada (ID: ${result.bank_account_id})\n` +
              `📊 Transações sincronizadas: ${result.transactions_synced}\n` +
              `💵 Saldo atual: R$ ${result.current_balance.toFixed(2)}\n\n` +
              `Acesse "Contas Bancárias" para ver os detalhes!`)

        // Recarregar dados após sincronização
        fetchGatewayData(gatewayId)
      }
    } catch (err: any) {
      console.error('Erro ao sincronizar com banco:', err)
      alert(`❌ Erro na sincronização:\n${err.response?.data?.detail || err.message}`)
    } finally {
      setSyncingToBank(prev => ({ ...prev, [gatewayId]: false }))
    }
  }

  // Deletar gateway de pagamento
  const handleDeleteGateway = async (gatewayId: number, gatewayName: string) => {
    const confirmed = window.confirm(
      `⚠️ Tem certeza que deseja deletar o gateway "${gatewayName}"?\n\n` +
      `Esta ação não pode ser desfeita!`
    )

    if (!confirmed) return

    try {
      await paymentGatewayAPI.deleteGateway(gatewayId.toString())
      alert('✅ Gateway deletado com sucesso!')
      // Recarregar a lista de gateways
      fetchIntegrationsData()
    } catch (err: any) {
      console.error('Erro ao deletar gateway:', err)
      alert(`❌ Erro ao deletar gateway:\n${err.response?.data?.detail || err.message}`)
    }
  }

  // Editar gateway de pagamento
  const handleEditGateway = (gateway: any) => {
    // Open the modal with the gateway data for editing
    setEditingGateway(gateway) // Passa o gateway completo para edição
    setSelectedGateway(gateway.gateway_type)
    setShowGatewayModal(true)
  }

  // Toggle expandir/recolher gateway
  const toggleGatewayExpand = async (gatewayId: number) => {
    if (expandedGateway === gatewayId) {
      setExpandedGateway(null)
    } else {
      setExpandedGateway(gatewayId)
      // Buscar dados se ainda não foram carregados
      if (!gatewayData[gatewayId]) {
        await fetchGatewayData(gatewayId)
      }
    }
  }

  const handleSelectGateway = (gateway: string) => {
    setSelectedGateway(gateway)
    setShowGatewayModal(false)
    // Abre o modal específico do gateway
    if (gateway === 'asaas') {
      // Abrirá o modal ASAAS
      setShowGatewayModal(true)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'connected': return 'bg-green-100 text-green-800'
      case 'inactive':
      case 'disconnected': return 'bg-gray-100 text-gray-800'
      case 'error': return 'bg-red-100 text-red-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Ativo'
      case 'inactive': return 'Inativo'
      case 'connected': return 'Conectado'
      case 'disconnected': return 'Desconectado'
      case 'error': return 'Erro'
      case 'pending': return 'Pendente'
      default: return status
    }
  }

  const getIntegrationIcon = (type: string) => {
    switch (type) {
      case 'bank': return <BuildingLibraryIcon className="w-6 h-6" />
      case 'payment_gateway': return <CreditCardIcon className="w-6 h-6" />
      case 'accounting': return <DocumentTextIcon className="w-6 h-6" />
      case 'crm': return <UsersIcon className="w-6 h-6" />
      default: return <LinkIcon className="w-6 h-6" />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'bank': return 'Banco'
      case 'payment_gateway': return 'Gateway de Pagamento'
      case 'accounting': return 'Contabilidade'
      case 'crm': return 'CRM'
      case 'other': return 'Outros'
      default: return type
    }
  }

  // Mock data for demonstration
  const mockStats: IntegrationStats = {
    total_integrations: 8,
    active_integrations: 6,
    total_synced_records: 12540,
    sync_errors: 2,
    most_active_integration: 'Pluggy Banking',
    last_global_sync: '2025-09-21T15:30:00Z'
  }

  const mockIntegrations: Integration[] = [
    {
      id: 1,
      name: 'Pluggy Banking',
      type: 'bank',
      provider: 'Pluggy',
      status: 'active',
      last_sync: '2025-09-21T15:30:00Z',
      auto_sync: true,
      sync_frequency: 1440,
      config: {
        environment: 'production',
        webhook_url: 'https://finpro.com/webhooks/pluggy'
      },
      stats: {
        total_syncs: 156,
        last_sync_records: 24,
        total_records: 3420,
        error_count: 0
      },
      created_at: '2025-01-15T10:00:00Z'
    },
    {
      id: 2,
      name: 'Asaas Gateway',
      type: 'payment_gateway',
      provider: 'Asaas',
      status: 'active',
      last_sync: '2025-09-21T14:45:00Z',
      auto_sync: true,
      sync_frequency: 720,
      config: {
        environment: 'production',
        webhook_url: 'https://finpro.com/webhooks/asaas'
      },
      stats: {
        total_syncs: 89,
        last_sync_records: 12,
        total_records: 1580,
        error_count: 1
      },
      created_at: '2025-01-20T14:00:00Z'
    },
    {
      id: 3,
      name: 'OpenAI ChatGPT',
      type: 'other',
      provider: 'OpenAI',
      status: 'active',
      last_sync: '2025-09-21T16:00:00Z',
      auto_sync: false,
      sync_frequency: 0,
      config: {
        environment: 'production'
      },
      stats: {
        total_syncs: 45,
        last_sync_records: 0,
        total_records: 0,
        error_count: 0
      },
      created_at: '2025-02-01T09:00:00Z'
    }
  ]

  const mockBankConnections: BankConnection[] = [
    {
      id: 1,
      bank_name: 'Nubank',
      connection_status: 'connected',
      account_count: 2,
      last_transaction_sync: '2025-09-21T15:30:00Z',
      connector_id: 101
    },
    {
      id: 2,
      bank_name: 'Banco do Brasil',
      connection_status: 'connected',
      account_count: 1,
      last_transaction_sync: '2025-09-21T14:20:00Z',
      connector_id: 102
    },
    {
      id: 3,
      bank_name: 'Itaú',
      connection_status: 'error',
      account_count: 0,
      last_transaction_sync: '2025-09-20T10:00:00Z',
      connector_id: 103
    }
  ]

  const displayStats = stats || mockStats
  const displayIntegrations = integrations.length > 0 ? integrations : mockIntegrations
  const displayBankConnections = bankConnections.length > 0 ? bankConnections : mockBankConnections

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar integrações</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LinkIcon className="w-7 h-7 text-purple-600" />
            Integrações
          </h1>
          <p className="text-gray-600">Conecte sistemas externos e automatize processos</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Integração
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Integrações</p>
              <p className="text-2xl font-bold">{displayStats.total_integrations}</p>
            </div>
            <LinkIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Ativas</p>
              <p className="text-2xl font-bold">{displayStats.active_integrations}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Registros Sincronizados</p>
              <p className="text-2xl font-bold">{displayStats.total_synced_records.toLocaleString()}</p>
            </div>
            <ArrowPathIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Erros de Sync</p>
              <p className="text-2xl font-bold">{displayStats.sync_errors}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-red-200" />
          </div>
        </div>
      </div>

      {/* Pluggy Open Banking Card - DESTAQUE PRINCIPAL */}
      <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-8 text-white">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center shadow-lg">
                  <BanknotesIcon className="w-9 h-9 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-1">Pluggy Open Banking</h2>
                  <p className="text-indigo-100 text-lg">Integração bancária via Open Finance Brasil</p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-6 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" />
                    <span className="text-white/90">Conexão automática de contas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" />
                    <span className="text-white/90">Sincronização em tempo real</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" />
                    <span className="text-white/90">Principais bancos brasileiros</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" />
                    <span className="text-white/90">Segurança e criptografia</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/integrations/pluggy')}
                  className="bg-white text-indigo-600 px-8 py-4 rounded-lg hover:bg-indigo-50 font-bold text-lg flex items-center gap-3 transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  <BuildingLibraryIcon className="w-6 h-6" />
                  Acessar Gerenciador de Bancos
                </button>

                <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3 border border-white/20">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold">Sistema Ativo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Gateways Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                <CreditCardIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Gateways de Pagamento</h3>
                <p className="text-sm text-gray-600">Configure suas integrações com processadores de pagamento</p>
              </div>
            </div>
            <button
              onClick={() => setShowGatewayModal(true)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2 rounded-lg hover:from-emerald-700 hover:to-teal-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <PlusIcon className="w-5 h-5" />
              Adicionar Gateway
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paymentGateways.length === 0 ? (
              <div className="col-span-full bg-gray-50 rounded-lg p-8 text-center">
                <CreditCardIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">Nenhum gateway de pagamento configurado</p>
                <button
                  onClick={() => setShowGatewayModal(true)}
                  className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 inline-flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  Configurar Primeiro Gateway
                </button>
              </div>
            ) : (
              paymentGateways.map((gateway) => (
                <div key={gateway.id} className="border-2 border-gray-200 rounded-xl p-5 hover:shadow-xl hover:border-emerald-400 transition-all duration-200 bg-white">
                  {/* Header com Status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${
                        gateway.gateway_type === 'asaas' ? 'bg-gradient-to-br from-orange-500 to-orange-600' :
                        gateway.gateway_type === 'pagarme' ? 'bg-gradient-to-br from-green-500 to-teal-600' :
                        gateway.gateway_type === 'stripe' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                        'bg-gradient-to-br from-emerald-500 to-teal-500'
                      }`}>
                        <CreditCardIcon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">{gateway.name || gateway.gateway_name}</h4>
                        <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">{gateway.gateway_type}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${
                        gateway.is_active ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-gray-100 text-gray-800 border border-gray-300'
                      }`}>
                        {gateway.is_active ? '✓ Ativo' : '○ Inativo'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        gateway.environment === 'production' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {gateway.environment === 'production' ? '🟢 Produção' : '🟡 Sandbox'}
                      </span>
                    </div>
                  </div>

                  {/* Métodos de Pagamento (se disponível) */}
                  {gateway.config && (
                    <div className="mb-4 pb-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Métodos Habilitados</p>
                      <div className="flex flex-wrap gap-1.5">
                        {gateway.config.enable_pix && (
                          <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs font-medium border border-teal-200">
                            PIX
                          </span>
                        )}
                        {gateway.config.enable_boleto && (
                          <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs font-medium border border-orange-200">
                            Boleto
                          </span>
                        )}
                        {gateway.config.enable_credit_card && (
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium border border-blue-200">
                            Crédito
                          </span>
                        )}
                        {gateway.config.enable_debit_card && (
                          <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium border border-purple-200">
                            Débito
                          </span>
                        )}
                        {gateway.config.enable_split && (
                          <span className="px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs font-medium border border-pink-200">
                            Split
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Informações Detalhadas */}
                  <div className="space-y-2.5 mb-4">
                    {/* Webhook */}
                    {gateway.webhook_url && (
                      <div className="flex items-center gap-2 text-xs">
                        <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500">Webhook:</span>
                        <span className="font-medium text-gray-700 truncate flex-1" title={gateway.webhook_url}>
                          {gateway.webhook_url.replace(/^https?:\/\//, '').substring(0, 25)}...
                        </span>
                      </div>
                    )}

                    {/* Auto Sync */}
                    {gateway.auto_sync !== undefined && (
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <ArrowPathIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-500">Sincronização:</span>
                        </div>
                        <span className={`font-semibold ${gateway.auto_sync ? 'text-green-600' : 'text-gray-600'}`}>
                          {gateway.auto_sync ? `Auto (${gateway.sync_frequency || 60}min)` : 'Manual'}
                        </span>
                      </div>
                    )}

                    {/* Last Sync */}
                    {gateway.last_sync && (
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-500">Última Sync:</span>
                        </div>
                        <span className="font-medium text-gray-700">{formatDate(gateway.last_sync)}</span>
                      </div>
                    )}

                    {/* Last Sync Status */}
                    {gateway.last_sync_status && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Status Sync:</span>
                        <span className={`font-semibold ${
                          gateway.last_sync_status === 'success' ? 'text-green-600' :
                          gateway.last_sync_status === 'error' ? 'text-red-600' :
                          'text-yellow-600'
                        }`}>
                          {gateway.last_sync_status === 'success' ? '✓ Sucesso' :
                           gateway.last_sync_status === 'error' ? '✗ Erro' :
                           gateway.last_sync_status}
                        </span>
                      </div>
                    )}

                    {/* Virtual Accounts Support */}
                    {gateway.supports_virtual_accounts && (
                      <div className="flex items-center gap-2 text-xs">
                        <BanknotesIcon className="w-4 h-4 text-emerald-500" />
                        <span className="text-emerald-600 font-medium">Suporta contas virtuais</span>
                      </div>
                    )}

                    {/* Datas */}
                    <div className="pt-2 mt-2 border-t border-gray-100 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Criado em:</span>
                        <span className="text-gray-600 font-medium">{formatDate(gateway.created_at)}</span>
                      </div>
                      {gateway.updated_at && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Atualizado:</span>
                          <span className="text-gray-600 font-medium">{formatDate(gateway.updated_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/integrations/${gateway.gateway_type.toLowerCase()}/${gateway.id}`)}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 rounded-lg hover:from-blue-700 hover:to-indigo-700 text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        Ver Dashboard
                      </button>
                      <button
                        onClick={() => syncGatewayToBank(gateway.id)}
                        disabled={syncingToBank[gateway.id]}
                        className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2.5 rounded-lg hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {syncingToBank[gateway.id] ? (
                          <>
                            <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            Sincronizando...
                          </>
                        ) : (
                          <>
                            <BanknotesIcon className="w-4 h-4" />
                            Sync Banco
                          </>
                        )}
                      </button>
                    </div>

                    {/* Segunda linha de ações: Editar e Deletar */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditGateway(gateway)}
                        className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2.5 rounded-lg hover:from-yellow-600 hover:to-orange-600 text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteGateway(gateway.id, gateway.name || gateway.gateway_name)}
                        className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 text-white px-4 py-2.5 rounded-lg hover:from-red-700 hover:to-pink-700 text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Deletar
                      </button>
                    </div>

                    {/* Dados expandidos do gateway */}
                    {expandedGateway === gateway.id && gatewayData[gateway.id] && (
                      <div className="mt-4 border-t-2 border-gray-200 pt-4 space-y-4 animate-fade-in">
                        {/* Saldo */}
                        {gatewayData[gateway.id].balance && (
                          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-bold text-gray-900 flex items-center gap-2">
                                <BanknotesIcon className="w-5 h-5 text-emerald-600" />
                                Saldo Atual
                              </h5>
                              <button
                                onClick={() => fetchGatewayData(gateway.id)}
                                className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                              >
                                <ArrowPathIcon className="w-3 h-3" />
                                Atualizar
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo Total</p>
                                <p className="text-xl font-bold text-emerald-600">
                                  R$ {gatewayData[gateway.id].balance.balance?.toFixed(2) || '0.00'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Disponível</p>
                                <p className="text-xl font-bold text-teal-600">
                                  R$ {gatewayData[gateway.id].balance.available_balance?.toFixed(2) || '0.00'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Transações */}
                        {gatewayData[gateway.id].transactions.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                              <DocumentTextIcon className="w-5 h-5 text-gray-600" />
                              Últimas Transações (30 dias)
                            </h5>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {gatewayData[gateway.id].transactions.slice(0, 10).map((txn: any, idx: number) => (
                                <div key={idx} className="bg-white rounded p-3 border border-gray-200 hover:border-gray-300 transition-colors">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {txn.description || txn.id}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {txn.date ? new Date(txn.date).toLocaleDateString('pt-BR') : 'N/A'}
                                      </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className={`text-sm font-bold ${
                                        txn.amount >= 0 ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {txn.amount >= 0 ? '+' : '-'} R$ {Math.abs(txn.amount).toFixed(2)}
                                      </p>
                                      {txn.status && (
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          txn.status === 'CONFIRMED' || txn.status === 'RECEIVED' ? 'bg-green-100 text-green-700' :
                                          txn.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {txn.status}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {gatewayData[gateway.id].transactions.length > 10 && (
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                Mostrando 10 de {gatewayData[gateway.id].transactions.length} transações
                              </p>
                            )}
                          </div>
                        )}

                        {/* Se não houver transações */}
                        {gatewayData[gateway.id].transactions.length === 0 && (
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                            <DocumentTextIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Nenhuma transação nos últimos 30 dias</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Gateway Selection Modal */}
      {showGatewayModal && selectedGateway === null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Selecione o Gateway de Pagamento</h3>
              <button
                onClick={() => setShowGatewayModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* ASAAS */}
              <button
                onClick={() => setSelectedGateway('asaas')}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-orange-500 hover:shadow-lg transition-all text-left group"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    AS
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">ASAAS</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Gateway Brasileiro</p>
                  </div>
                </div>
                <div className="space-y-1 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>PIX e Boleto</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Cartões de Crédito/Débito</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Split de Pagamento</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Webhooks em tempo real</span>
                  </div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-3 py-2">
                  <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">🇧🇷 Recomendado para Brasil</p>
                </div>
              </button>

              {/* Pagar.me */}
              <button
                onClick={() => setSelectedGateway('pagarme')}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-green-500 hover:shadow-lg transition-all text-left group"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    PM
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">Pagar.me</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Stone Payments (API v5)</p>
                  </div>
                </div>
                <div className="space-y-1 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>PIX e Boleto</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Cartões Crédito/Débito</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Antifraude integrado</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>API v5 (Moderna)</span>
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
                  <p className="text-xs text-green-700 dark:text-green-300 font-medium">🇧🇷 Gateway Brasileiro</p>
                </div>
              </button>

              {/* Stripe */}
              <button
                onClick={() => alert('Stripe em breve!')}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition-all text-left group opacity-60"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    ST
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">Stripe</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Gateway Internacional</p>
                  </div>
                </div>
                <div className="space-y-1 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Cartões Internacionais</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Apple Pay / Google Pay</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>135+ moedas</span>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">🚧 Em breve</p>
                </div>
              </button>

              {/* Mercado Pago */}
              <button
                onClick={() => alert('Mercado Pago em breve!')}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-cyan-500 hover:shadow-lg transition-all text-left group opacity-60"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    MP
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">Mercado Pago</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">América Latina</p>
                  </div>
                </div>
                <div className="space-y-1 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>PIX e Boleto</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Carteiras digitais</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>QR Code</span>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">🚧 Em breve</p>
                </div>
              </button>

              {/* PagSeguro */}
              <button
                onClick={() => alert('PagSeguro em breve!')}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-green-500 hover:shadow-lg transition-all text-left group opacity-60"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    PS
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">PagSeguro</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">UOL / PagBank</p>
                  </div>
                </div>
                <div className="space-y-1 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>PIX e Boleto</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Cartões</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Débito online</span>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">🚧 Em breve</p>
                </div>
              </button>

              {/* Cielo */}
              <button
                onClick={() => alert('Cielo em breve!')}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-indigo-500 hover:shadow-lg transition-all text-left group opacity-60"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    CI
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg">Cielo</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Líder em Adquirência</p>
                  </div>
                </div>
                <div className="space-y-1 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>PIX e Boleto</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Cartões Visa/Master</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Link de Pagamento</span>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">🚧 Em breve</p>
                </div>
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowGatewayModal(false)}
                className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ASAAS Configuration Modal */}
      <AsaasConfigModal
        isOpen={selectedGateway === 'asaas'}
        onClose={() => {
          setSelectedGateway(null)
          setEditingGateway(null)
          setShowGatewayModal(false)
        }}
        onSuccess={handleGatewaySuccess}
        existingGateway={editingGateway}
      />

      {/* PAGAR.ME Configuration Modal */}
      <PagarmeConfigModal
        isOpen={selectedGateway === 'pagarme'}
        onClose={() => {
          setSelectedGateway(null)
          setEditingGateway(null)
          setShowGatewayModal(false)
        }}
        onSuccess={handleGatewaySuccess}
        existingGateway={editingGateway}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Nova Integração</h3>
            <form className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Integração *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Nome identificador da integração"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Integração *
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="bank">Banco</option>
                    <option value="payment_gateway">Gateway de Pagamento</option>
                    <option value="accounting">Contabilidade</option>
                    <option value="crm">CRM</option>
                    <option value="other">Outros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provedor *
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Selecione o provedor</option>
                    <optgroup label="Bancos">
                      <option value="Pluggy">Pluggy (Open Banking)</option>
                      <option value="Belvo">Belvo</option>
                      <option value="Yapily">Yapily</option>
                    </optgroup>
                    <optgroup label="Gateways de Pagamento">
                      <option value="Asaas">Asaas</option>
                      <option value="Stripe">Stripe</option>
                      <option value="PagarMe">Pagar.me</option>
                      <option value="MercadoPago">Mercado Pago</option>
                      <option value="PagSeguro">PagSeguro</option>
                      <option value="Moip">Moip</option>
                      <option value="Cielo">Cielo</option>
                    </optgroup>
                    <optgroup label="IA/Serviços">
                      <option value="OpenAI">OpenAI</option>
                      <option value="Anthropic">Anthropic</option>
                    </optgroup>
                    <optgroup label="CRM">
                      <option value="Salesforce">Salesforce</option>
                      <option value="HubSpot">HubSpot</option>
                      <option value="Pipedrive">Pipedrive</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ambiente
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="sandbox">Sandbox (Teste)</option>
                    <option value="production">Produção</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key / Client ID *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Chave da API fornecida pelo provedor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Secret / Client Secret
                  </label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Chave secreta (se necessário)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL do Webhook (opcional)
                  </label>
                  <input
                    type="url"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="https://seudominio.com/webhooks/..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sincronização Automática
                    </label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="true">Habilitada</option>
                      <option value="false">Desabilitada</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequência (minutos)
                    </label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="15">15 minutos</option>
                      <option value="30">30 minutos</option>
                      <option value="60">1 hora</option>
                      <option value="360">6 horas</option>
                      <option value="720">12 horas</option>
                      <option value="1440">24 horas</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Configurações Adicionais (JSON)
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={3}
                    placeholder='{"timeout": 30, "retries": 3}'
                  ></textarea>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Ativar integração após criação</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Testar conexão</span>
                </label>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">Importante</h4>
                    <p className="text-sm text-yellow-700">
                      Mantenha suas credenciais de API seguras. Elas serão criptografadas e armazenadas de forma segura.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Criar Integração
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default IntegrationsPage